import * as formik from "formik";
import * as yup from "yup";

import { Col, Form, Modal, Row } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppButton from "../../../commons/buttons/AppButton";
import AppCurrencyInput from "../../../commons/inputs/AppCurrencyInput";
import AppInput from "../../../commons/inputs/AppInput";
import AppLinkButton from "../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../commons/AppLoading";
import AppTextAreaInput from "../../../commons/inputs/AppTextAreaInput";
import HallAppToast from "../../../commons/HallAppToast";
import ProductInventoryMovementAPI from "../ProductInventoryMovementAPI";
import SelectBasicInput from "../../../commons/inputs/SelectBasicInput";

const movementAPI = new ProductInventoryMovementAPI();

const movementOptions = [
  { key: "INPUT", label: "Entrada" },
  { key: "OUTPUT", label: "Saída" },
  { key: "ADJUSTMENT", label: "Ajuste" },
];

const movementReasonOptionsByType = {
  INPUT: [
    { key: "PURCHASE", label: "Compra / reposição" },
    { key: "INITIAL_STOCK", label: "Estoque inicial" },
    { key: "RETURN", label: "Devolução" },
    { key: "MANUAL_ADJUSTMENT", label: "Ajuste manual" },
  ],
  OUTPUT: [
    { key: "CONSUMPTION", label: "Consumo interno" },
    { key: "LOSS", label: "Perda / avaria / vencimento" },
    { key: "RETURN_SUPPLIER", label: "Devolução ao fornecedor" },
    { key: "MANUAL_ADJUSTMENT", label: "Ajuste manual" },
  ],
  ADJUSTMENT: [
    { key: "INVENTORY_COUNT", label: "Contagem / inventário" },
    { key: "MANUAL_ADJUSTMENT", label: "Ajuste manual" },
    { key: "SYSTEM_CORRECTION", label: "Correção de cadastro" },
  ],
};

const commissionTypeOptions = [
  { key: "PERCENT", label: "Percentual (%)" },
  { key: "FIXED", label: "Valor fixo (R$)" },
];

function ModalProductStock({ closeShow, show, productToEdit, callGetProducts }) {
  const [isLoading, setIsLoading] = useState(false);

  const [initialValues, setInitialValues] = useState({
    movementType: "INPUT",
    movementReason: "PURCHASE",
    quantity: "",
    minQuantity: "",
    reason: "",
    observation: "",

    unitCostPrice: "",
    totalCostPrice: "",

    updatePricing: false,

    pricingCostPrice: "",
    pricingCustomerSalePrice: "",
    pricingEmployeeSalePrice: "",
    pricingPromotionalPrice: "",
    pricingEmployeeCommissionType: "PERCENT",
    pricingEmployeeCommissionValue: "",
  });

  const { Formik } = formik;

  const schema = yup.object().shape({
    movementType: yup.string().required("Selecione o tipo da movimentação"),
    movementReason: yup.string().required("Selecione o motivo da movimentação"),
    quantity: yup
      .number()
      .typeError("Informe a quantidade")
      .required("Informe a quantidade")
      .integer("A quantidade deve ser um número inteiro")
      .moreThan(0, "A quantidade deve ser maior que zero"),
    minQuantity: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? null : value
      )
      .nullable()
      .min(0, "O estoque mínimo não pode ser negativo"),
    reason: yup.string().max(100, "Motivo deve ter no máximo 100 caracteres"),
    observation: yup.string().max(500, "Observação deve ter no máximo 500 caracteres"),

    unitCostPrice: yup.string().when("movementType", {
      is: "INPUT",
      then: (schemaValue) => schemaValue,
      otherwise: (schemaValue) => schemaValue,
    }),

    totalCostPrice: yup.string(),

    updatePricing: yup.boolean(),

    pricingCostPrice: yup.string(),
    pricingCustomerSalePrice: yup.string(),
    pricingEmployeeSalePrice: yup.string(),
    pricingPromotionalPrice: yup.string(),
    pricingEmployeeCommissionType: yup.string(),
    pricingEmployeeCommissionValue: yup.string(),
  });

  useEffect(() => {
    if (show) {
      setInitialValues({
        movementType: "INPUT",
        movementReason: "PURCHASE",
        quantity: "",
        minQuantity: productToEdit?.inventory?.minQuantity ?? productToEdit?.minQuantity ?? "",
        reason: "",
        observation: "",

        unitCostPrice:
          productToEdit?.pricing?.costPrice ?? productToEdit?.costPrice ?? "",
        totalCostPrice: "",

        updatePricing: false,

        pricingCostPrice:
          productToEdit?.pricing?.costPrice ?? productToEdit?.costPrice ?? "",
        pricingCustomerSalePrice:
          productToEdit?.pricing?.customerSalePrice ??
          productToEdit?.customerSalePrice ??
          "",
        pricingEmployeeSalePrice:
          productToEdit?.pricing?.employeeSalePrice ??
          productToEdit?.employeeSalePrice ??
          "",
        pricingPromotionalPrice:
          productToEdit?.pricing?.promotionalPrice ?? "",
        pricingEmployeeCommissionType:
          productToEdit?.pricing?.employeeCommissionType ||
          productToEdit?.employeeCommissionType ||
          "PERCENT",
        pricingEmployeeCommissionValue:
          productToEdit?.pricing?.employeeCommissionValue ??
          productToEdit?.employeeCommissionValue ??
          "",
      });
    }
  }, [show, productToEdit]);

  const toNumber = (v) => {
    if (typeof v === "number") return v;
    if (v == null || v === "") return 0;

    const normalized = String(v)
      .replace(/\s/g, "")
      .replace(/\./g, "")
      .replace(",", ".")
      .replace(/[^\d.-]/g, "");

    return Number(normalized) || 0;
  };

  const toNullableNumber = (v) => {
    if (v === null || v === undefined || v === "") return null;
    return toNumber(v);
  };

  const formatCurrencyValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return Number(value).toFixed(2);
  };

  const formatMoneyLabel = (value) => {
    if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
      return "R$ 0,00";
    }

    return Number(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatCommissionLabel = (type, value) => {
    if (value === null || value === undefined || value === "") {
      return "Não definida";
    }

    if (type === "PERCENT") {
      return `${Number(value)}%`;
    }

    return formatMoneyLabel(value);
  };

  const calculateTotalCost = (quantity, unitCostPrice) => {
    const qtd = toNumber(quantity);
    const unit = toNumber(unitCostPrice);

    if (!qtd || !unit) return "";
    return Number((qtd * unit).toFixed(2));
  };

  const calculateUnitCost = (quantity, totalCostPrice) => {
    const qtd = toNumber(quantity);
    const total = toNumber(totalCostPrice);

    if (!qtd || !total) return "";
    return Number((total / qtd).toFixed(2));
  };

  const modalTitle = useMemo(() => {
    if (!productToEdit?.name) return "MOVIMENTAR ESTOQUE";
    return `MOVIMENTAR ESTOQUE - ${productToEdit.name}`;
  }, [productToEdit]);


  const save = async (values) => {
    if (!productToEdit?.id) {
      HallAppToast.error("Produto inválido para movimentação.");
      return;
    }

    const unitCostPrice =
      values.movementType === "INPUT" && values.unitCostPrice !== ""
        ? toNumber(values.unitCostPrice)
        : null;

    const totalCostPrice =
      values.movementType === "INPUT" && values.totalCostPrice !== ""
        ? toNumber(values.totalCostPrice)
        : null;

    const pricing =
      values.updatePricing
        ? {
            costPrice: toNullableNumber(values.pricingCostPrice),
            customerSalePrice: toNullableNumber(values.pricingCustomerSalePrice),
            employeeSalePrice: toNullableNumber(values.pricingEmployeeSalePrice),
            promotionalPrice: toNullableNumber(values.pricingPromotionalPrice),
            employeeCommissionType: values.pricingEmployeeCommissionType || null,
            employeeCommissionValue: toNullableNumber(values.pricingEmployeeCommissionValue),
          }
        : null;

        const data = {
          companyId: productToEdit.companyId,
          productId: productToEdit.id,
          movementType: values.movementType,
          movementReason: values.movementReason,
          originType: "MANUAL",
          originId: null,
          quantity: Number(values.quantity),
          minQuantity:
            values.minQuantity === "" || values.minQuantity === null
              ? null
              : Number(values.minQuantity),
          reason: values.reason || null,
          observation: values.observation || null,
          unitCostPrice,
          totalCostPrice,
          updatePricing: !!values.updatePricing,
          pricing,
        };

    setIsLoading(true);
    try {
      await movementAPI.create(data);
      HallAppToast.successWithMessage("Movimentação realizada com sucesso.");
      if (callGetProducts) await callGetProducts();
      closeShow();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} fullscreen={true} onHide={() => closeShow()}>
      <Modal.Header className="custom-close-button" closeButton>
        <Modal.Title style={{ textAlign: "center", fontSize: "15px" }}>
          {modalTitle}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Formik
          validationSchema={schema}
          onSubmit={save}
          initialValues={initialValues}
          enableReinitialize={true}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            setFieldValue,
          }) => {
            const reasonOptions =
              movementReasonOptionsByType[values.movementType] || movementReasonOptionsByType.INPUT;

            return (
              <Form noValidate onSubmit={handleSubmit}>
                <div className="container">
                  <Row>
                    <Col xs={12}>
                      <SelectBasicInput
                        errors={errors}
                        touched={touched}
                        name="movementType"
                        onChange={(e) => {
                          const movementType = e.target.value;
                          const nextReason =
                            (movementReasonOptionsByType[movementType] || [])[0]?.key || "";

                          setFieldValue("movementType", movementType);
                          setFieldValue("movementReason", nextReason);

                          if (movementType !== "INPUT") {
                            setFieldValue("unitCostPrice", "");
                            setFieldValue("totalCostPrice", "");
                          }
                        }}
                        value={values.movementType}
                        options={movementOptions}
                        label="Tipo de movimentação"
                        isRequired
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12}>
                      <SelectBasicInput
                        errors={errors}
                        touched={touched}
                        name="movementReason"
                        onChange={handleChange}
                        value={values.movementReason}
                        options={reasonOptions}
                        label="Motivo da movimentação"
                        isRequired
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={6}>
                      <AppInput
                        errors={errors}
                        isRequired
                        touched={touched}
                        name="quantity"
                        onChange={(e) => {
                          handleChange(e);

                          if (values.movementType === "INPUT") {
                            const nextQuantity = e.target.value;

                            if (values.unitCostPrice) {
                              const totalCost = calculateTotalCost(
                                nextQuantity,
                                values.unitCostPrice
                              );
                              setFieldValue(
                                "totalCostPrice",
                                formatCurrencyValue(totalCost),
                                false
                              );
                            } else if (values.totalCostPrice) {
                              const unitCost = calculateUnitCost(
                                nextQuantity,
                                values.totalCostPrice
                              );
                              setFieldValue(
                                "unitCostPrice",
                                formatCurrencyValue(unitCost),
                                false
                              );
                            }
                          }
                        }}
                        value={values.quantity}
                        type="number"
                        label="Quantidade"
                      />
                    </Col>

                    <Col xs={12} md={6}>
                      <AppInput
                        errors={errors}
                        touched={touched}
                        name="minQuantity"
                        onChange={handleChange}
                        value={values.minQuantity}
                        type="number"
                        label="Estoque mínimo"
                        formText="Opcional. Atualize aqui o mínimo ideal para alerta."
                      />
                    </Col>
                  </Row>

                  {values.movementType === "INPUT" && (
                    <>
                      <hr />

                      <h6 style={{ fontWeight: 700, color: "#2f2a7a", marginBottom: 16 }}>
                        Dados da entrada
                      </h6>

                      <Row>
                        <Col xs={12} md={6}>
                          <AppCurrencyInput
                            errors={errors}
                            touched={touched}
                            name="unitCostPrice"
                            value={values.unitCostPrice}
                            type="text"
                            label="Custo unitário"
                            onChange={(_, value) => {
                              setFieldValue("unitCostPrice", value);

                              const totalCost = calculateTotalCost(values.quantity, value);
                              setFieldValue(
                                "totalCostPrice",
                                formatCurrencyValue(totalCost),
                                false
                              );

                              if (!values.updatePricing && !values.pricingCostPrice) {
                                setFieldValue("pricingCostPrice", value, false);
                              }
                            }}
                          />
                        </Col>

                        <Col xs={12} md={6}>
                          <AppCurrencyInput
                            errors={errors}
                            touched={touched}
                            name="totalCostPrice"
                            value={values.totalCostPrice}
                            type="text"
                            label="Custo total"
                            onChange={(_, value) => {
                              setFieldValue("totalCostPrice", value);

                              const unitCost = calculateUnitCost(values.quantity, value);
                              setFieldValue(
                                "unitCostPrice",
                                formatCurrencyValue(unitCost),
                                false
                              );
                            }}
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col xs={12}>
                          <Form.Check
                            id="updatePricing"
                            type="switch"
                            label="Atualizar também a configuração de preço do produto"
                            checked={!!values.updatePricing}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFieldValue("updatePricing", checked);

                              if (
                                checked &&
                                values.unitCostPrice &&
                                !values.pricingCostPrice
                              ) {
                                setFieldValue("pricingCostPrice", values.unitCostPrice, false);
                              }
                            }}
                          />
                        </Col>
                      </Row>

                      {values.updatePricing && (
                        <>
                          <hr />

                          <h6 style={{ fontWeight: 700, color: "#2f2a7a", marginBottom: 16 }}>
                            Configuração atual de preço
                          </h6>

                          <Row>
                            <Col xs={12} md={6}>
                              <AppCurrencyInput
                                errors={errors}
                                touched={touched}
                                name="pricingCostPrice"
                                value={values.pricingCostPrice}
                                type="text"
                                label="Custo atual"
                                onChange={(_, value) => {
                                  setFieldValue("pricingCostPrice", value);
                                }}
                              />
                            </Col>

                            <Col xs={12} md={6}>
                              <AppCurrencyInput
                                errors={errors}
                                touched={touched}
                                name="pricingCustomerSalePrice"
                                value={values.pricingCustomerSalePrice}
                                type="text"
                                label="Preço para cliente"
                                onChange={(_, value) => {
                                  setFieldValue("pricingCustomerSalePrice", value);
                                }}
                              />
                            </Col>
                          </Row>

                          <Row>
                            <Col xs={12} md={6}>
                              <AppCurrencyInput
                                errors={errors}
                                touched={touched}
                                name="pricingEmployeeSalePrice"
                                value={values.pricingEmployeeSalePrice}
                                type="text"
                                label="Preço para profissional"
                                onChange={(_, value) => {
                                  setFieldValue("pricingEmployeeSalePrice", value);
                                }}
                              />
                            </Col>

                            <Col xs={12} md={6}>
                              <AppCurrencyInput
                                errors={errors}
                                touched={touched}
                                name="pricingPromotionalPrice"
                                value={values.pricingPromotionalPrice}
                                type="text"
                                label="Preço promocional"
                                onChange={(_, value) => {
                                  setFieldValue("pricingPromotionalPrice", value);
                                }}
                              />
                            </Col>
                          </Row>

                          <Row>
                            <Col xs={12} md={6}>
                              <SelectBasicInput
                                errors={errors}
                                touched={touched}
                                name="pricingEmployeeCommissionType"
                                onChange={handleChange}
                                value={values.pricingEmployeeCommissionType}
                                options={commissionTypeOptions}
                                label="Tipo de comissão padrão"
                              />
                            </Col>

                            <Col xs={12} md={6}>
                              {values.pricingEmployeeCommissionType === "FIXED" ? (
                                <AppCurrencyInput
                                  errors={errors}
                                  touched={touched}
                                  name="pricingEmployeeCommissionValue"
                                  value={values.pricingEmployeeCommissionValue}
                                  type="text"
                                  label="Comissão padrão"
                                  onChange={(_, value) => {
                                    setFieldValue("pricingEmployeeCommissionValue", value);
                                  }}
                                />
                              ) : (
                                <AppInput
                                  errors={errors}
                                  touched={touched}
                                  name="pricingEmployeeCommissionValue"
                                  onChange={handleChange}
                                  value={values.pricingEmployeeCommissionValue}
                                  type="number"
                                  label="Comissão padrão (%)"
                                />
                              )}
                            </Col>
                          </Row>

                          <div
                            style={{
                              marginTop: 20,
                              border: "1px solid #e6e1ff",
                              background: "#f8f6ff",
                              borderRadius: 16,
                              padding: 16,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 700,
                                color: "#2f2a7a",
                                fontSize: 15,
                                marginBottom: 12,
                              }}
                            >
                              Resumo da configuração
                            </div>

                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                                fontSize: 14,
                              }}
                            >
                              <div>
                                <strong>Custo atual:</strong>{" "}
                                {values.pricingCostPrice
                                  ? formatMoneyLabel(values.pricingCostPrice)
                                  : "Não informado"}
                              </div>

                              <div>
                                <strong>Preço para cliente:</strong>{" "}
                                {values.pricingCustomerSalePrice
                                  ? formatMoneyLabel(values.pricingCustomerSalePrice)
                                  : "Não informado"}
                              </div>

                              <div>
                                <strong>Preço para profissional:</strong>{" "}
                                {values.pricingEmployeeSalePrice
                                  ? formatMoneyLabel(values.pricingEmployeeSalePrice)
                                  : "Não informado"}
                              </div>

                              <div>
                                <strong>Preço promocional:</strong>{" "}
                                {values.pricingPromotionalPrice
                                  ? formatMoneyLabel(values.pricingPromotionalPrice)
                                  : "Não informado"}
                              </div>

                              <div>
                                <strong>Comissão padrão:</strong>{" "}
                                {formatCommissionLabel(
                                  values.pricingEmployeeCommissionType,
                                  values.pricingEmployeeCommissionValue
                                )}
                              </div>

                              <div
                                style={{
                                  marginTop: 4,
                                  paddingTop: 12,
                                  borderTop: "1px dashed #d9cffd",
                                  color: "#6b7280",
                                }}
                              >
                                Essa comissão é apenas a configuração vigente do produto.
                                O valor real da comissão deve ser calculado e salvo na venda.
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  <hr />

                  <Row>
                    <Col xs={12}>
                      <AppInput
                        errors={errors}
                        touched={touched}
                        name="reason"
                        onChange={handleChange}
                        value={values.reason}
                        type="text"
                        label="Descrição curta"
                        formText="Ex: compra no fornecedor X, contagem de estoque, avaria, uso interno"
                      />
                    </Col>
                  </Row>

                  <AppTextAreaInput
                    errors={errors}
                    touched={touched}
                    name="observation"
                    onChange={handleChange}
                    value={values.observation}
                    type="text"
                    label="Observação"
                  />

                  {isLoading ? (
                    <div style={{ textAlign: "center" }}>
                      <AppLoading />
                    </div>
                  ) : (
                    <AppButton type="submit" width="100%" name="salvar movimentação" />
                  )}

                  <div style={{ marginTop: "10px" }}>
                    <AppLinkButton name="fechar" onClick={closeShow} />
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

export default ModalProductStock;